import {
  AlertDialog,
  Badge,
  BodyText,
  Collapsible,
  Icons,
  Separator,
} from "@/components/ui";

import { FHIRIdentifierCopier } from "@/components/DebugTools/FHIRIdentifierCopier";
import { TRACKERS } from "@/constants/Trackers";
import { formatDateTime } from "@/lib/time";
import { trackEvent } from "@/lib/tracking";
import { MedicationRequest } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import {
  Check,
  CirclePlay,
  CircleSlash,
  CircleX,
  ClipboardPlus,
  Clock5,
  LoaderCircle,
  Octagon,
  SquareCheck,
  User,
} from "lucide-react";
import { useRef, useState } from "react";
import {
  Action,
  AdherenceStatus,
  type PatientMedication,
  useConfirmMedication,
  useMarkMedicationStatus,
} from "../../network/useMedications";
import { PrescriptionInfo } from "./PrescriptionInfo";
import { UpdateMedicationForm } from "./UpdateMedicationForm";

interface MedicationEntryProps {
  medication: PatientMedication;
  adherenceStatus: AdherenceStatus;
  isBatchConfirmingMedications: boolean;
}

export const MedicationEntry = ({
  medication,
  adherenceStatus,
  isBatchConfirmingMedications,
}: MedicationEntryProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const [showVoidPrompt, setShowVoidPrompt] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const {
    isSavingStatus,
    markMedicationActive,
    markMedicationCompleted,
    markMedicationIntended,
    markMedicationNotTaken,
    markMedicationStopped,
    markMedicationVoided,
  } = useMarkMedicationStatus({
    patientId,
    medicationStatementId: medication.medicationStatement.id as string,
  });

  const { isPending: isConfirming, mutate: confirmMedication } =
    useConfirmMedication({
      patientId,
      medicationStatementId: medication.medicationStatement.id as string,
    });

  const badges = [];

  if (
    medication.medicationRequest?.dosespotStatus ===
    MedicationRequest.DOSESPOT_STATUS.values.ERROR
  ) {
    badges.push(
      <Badge variant="error" key={`${medication.medicationStatement.id}-error`}>
        <Icons.TriangleAlert className="h-2 w-2 stroke-error" />
        <BodyText textColor="error" size="sm">
          Error
        </BodyText>
      </Badge>,
    );
  }
  if (medication.medicationRequest) {
    badges.push(
      <Badge
        variant="default"
        key={`${medication.medicationStatement.id}-prescribed`}
      >
        <Icons.Pill className="h-2 w-2 stroke-tertiary" />
        <BodyText
          textColor="tertiary"
          size="sm"
          className="flex align-middle items-center"
        >
          Prescribed
        </BodyText>
        <FHIRIdentifierCopier id={medication.medicationRequest.id as string} />
      </Badge>,
    );
  } else {
    badges.push(
      <Badge
        variant="default"
        key={`${medication.medicationStatement.id}-reported`}
      >
        <User className="h-2 w-2 stroke-tertiary" />
        <BodyText
          textColor="tertiary"
          size="sm"
          className="flex align-middle items-center"
        >
          Pt. Reported
        </BodyText>
      </Badge>,
    );
  }

  if (medication.needsConfirmation) {
    badges.push(
      <Badge
        variant="warning"
        key={`${medication.medicationStatement.id}-unconfirmed`}
      >
        <Icons.LoaderCircle className="h-2 w-2 stroke-warning" />
        <BodyText
          textColor="warning"
          size="sm"
          className="flex align-middle items-center"
        >
          Unconfirmed
        </BodyText>
      </Badge>,
    );
  }

  const actionMenuItems = [];
  if (
    !isConfirming &&
    !isBatchConfirmingMedications &&
    medication.needsConfirmation &&
    medication.availableConfirmationActions.find(
      (action) => action.action === Action.CONFIRM && !action.unavailableReason,
    )
  ) {
    actionMenuItems.push({
      label: "Confirm",
      icon: <Check size={16} />,
      onClick: confirmMedication,
    });
  }

  if (isSavingStatus || isConfirming) {
    actionMenuItems.push({
      label: "Saving...",
      icon: <LoaderCircle size={16} className="animate-spin" />,
      onClick: () => {},
    });
  }

  const forcedDropdownActions = [
    {
      label: "Active",
      icon: <CirclePlay className="mr-2" size={16} />,
      onClick: () => {
        trackEvent(TRACKERS.MARK_MEDICATION_AS_ACTIVE);
        markMedicationActive();
      },
      disabled: Boolean(
        medication.availableStatusChangeActions.find(
          (action) =>
            action.action === Action.ACTIVE && !!action.unavailableReason,
        ),
      ),
    },
    {
      label: "Intended",
      icon: <Clock5 className="mr-2" size={16} />,
      onClick: markMedicationIntended,
      disabled: Boolean(
        medication.availableStatusChangeActions.find(
          (action) =>
            action.action === Action.INTENDED && !!action.unavailableReason,
        ),
      ),
    },
    {
      label: "Not Taken",
      icon: <CircleSlash className="mr-2" size={16} />,
      onClick: markMedicationNotTaken,
      disabled: Boolean(
        medication.availableStatusChangeActions.find(
          (action) =>
            action.action === Action.NOT_TAKEN && !!action.unavailableReason,
        ),
      ),
    },
    {
      label: "Stop",
      icon: <Octagon className="mr-2" size={16} />,
      onClick: markMedicationStopped,
      disabled: Boolean(
        medication.availableStatusChangeActions.find(
          (action) =>
            action.action === Action.STOPPED && !!action.unavailableReason,
        ),
      ),
    },
    {
      label: "Completed",
      icon: <SquareCheck className="mr-2" size={16} />,
      onClick: markMedicationCompleted,
      disabled: Boolean(
        medication.availableStatusChangeActions.find(
          (action) =>
            action.action === Action.COMPLETE && !!action.unavailableReason,
        ),
      ),
    },
    {
      label: "Void",
      icon: <CircleX className="mr-2" size={16} />,
      onClick: () => {
        trackEvent(TRACKERS.VOID_MEDICATION);
        setShowVoidPrompt(true);
      },
      disabled: Boolean(
        medication.availableStatusChangeActions.find(
          (action) =>
            action.action === Action.VOID && !!action.unavailableReason,
        ),
      ),
    },
  ];

  return (
    <>
      <div ref={ref} />
      <Collapsible
        title={medication.medicationStatement.medicationName}
        headerSize="sm"
        actionMenu={actionMenuItems}
        forcedDropdownActions={!isSavingStatus ? forcedDropdownActions : []}
        badgeComponents={badges}
        open={isOpen}
        onOpenChange={setIsOpen}
        controlled
        background={
          adherenceStatus === AdherenceStatus.ACTIVE ? "default" : "secondary"
        }
        status={
          adherenceStatus === AdherenceStatus.STOPPED
            ? medication.medicationStatement.statusLabel
            : undefined
        }
        titleAction={
          <FHIRIdentifierCopier
            id={medication.medicationStatement.id as string}
          />
        }
      >
        <div className="flex flex-col gap-3">
          {medication.medicationRequest && (
            <>
              <div className="h-auto">
                <PrescriptionInfo
                  medicationRequest={medication.medicationRequest}
                />
              </div>
              <Separator />
            </>
          )}
          <UpdateMedicationForm
            medicationStatement={medication.medicationStatement}
            patientId={patientId}
            key={`${medication.medicationStatement.id}-${medication.medicationStatement.meta?.versionId}`}
          />
          {!medication.medicationRequest &&
            medication.medicationStatement.informationSource?.display && (
              <div className="flex flex-row gap-1 items-center">
                <ClipboardPlus size={16} className="stroke-tertiary" />
                <BodyText size="xs" textColor="tertiary">
                  Added by{" "}
                  {medication.medicationStatement.informationSource?.display} on{" "}
                  {formatDateTime(medication.medicationStatement.dateAsserted)}
                </BodyText>
              </div>
            )}
        </div>
      </Collapsible>
      <AlertDialog
        isOpen={showVoidPrompt}
        onOpenChange={setShowVoidPrompt}
        isControlled
        title="Void Documented Medication"
        body={`Voiding this will permanently remove it from the patient's medical record, but not the pharmacy's system. Consider choosing stopped or completed instead. Void should only be used if the medication was entered incorrectly (i.e. charted on wrong patient).`}
        actionLabel="Void"
        cancelLabel="Cancel"
        onAction={() => {
          markMedicationVoided();
          setShowVoidPrompt(false);
        }}
        onCancel={() => {
          setShowVoidPrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </>
  );
};
